import React from "react";
import { Container, Row } from "react-bootstrap";
import { DndContext, pointerWithin } from "@dnd-kit/core";
import { DragOverlay } from "@dnd-kit/core";

import LeadAPI from "../../api/LeadAPI";
import ViewController from "../../controller/ViewController";
import ApiUiController from "../../controller/ApiUiController";
import Const from "../../util/Constants";
import Util from "../../util/Util";
import KanbanColumn from "./KanbanColumn";
import FieldController from "../../controller/FieldController";
import KanbanCard from "./KanbanCard";
import GenericModelAPI from "../../api/GenericModelAPI";
import "./Kanban.css";

class KanbanUI extends React.Component {
  constructor(props) {
    super(props);

    this.state = { grouped: {}, totalCountByColumn: {}, loadingColumns: {}, pageByColumn: {}, hasMoreByColumn: {}, activeDrag: null };
    this.columnObservers = {};
    this.columnEndRefs = {};
    this.columnScrollContainers = {};

    this.columnPageCache = {};
  }

  componentDidMount() {
    this.groupData();
  }

  componentDidUpdate(prevProps) {
    if ( prevProps.selectedKanbanView !== this.props.selectedKanbanView ) {
      this.columnPageCache = {}; 
      this.groupData()
    };
  }

  componentWillUnmount() {
    Object.values(this.columnObservers || {}).forEach(obs => obs?.disconnect());
  }

  loadInitialColumns = () => {
    const { grouped } = this.state;
    Object.keys(grouped).forEach(key => {
      this.fetchColumnData(key, 1);
      this.fetchColumnCount(key);
    });
  };
  
  fetchColumnCount =(key) => {
    const { list, restrict } = this.props;
    const kanbanField = this.props.kanbanViews?.find((item) => item.id === this.props.selectedKanbanView?.value).group_by;
    const kanbanFilter = {
      field: kanbanField,
      type: "select",
      condition: "in",
      value: `~${key}~`,
    };

    const mainFilters = ViewController.getRestrictionsView(Const.Lead);
    const filters = ViewController.filters([...list.filters, kanbanFilter], [], mainFilters, restrict);
    const filtersObj = {
      ...list,
      filters,
      pagination: {
        currentPage: 1,
      },
    };
    LeadAPI.count( filtersObj, (res) =>  this.onCloumnCountSuccess(key, res), (res) => this.onColumnFailed(key, res));
  }

  fetchColumnData = (key, page) => {
    const { list, view, restrict } = this.props;
    const { loadingColumns } = this.state;

    if (loadingColumns[key]) return;

    const kanbanField = this.props.kanbanViews?.find((item) => item.id === this.props.selectedKanbanView?.value).group_by;
    const kanbanFilter = {
      field: kanbanField,
      type: "select",
      condition: "in",
      value: `~${key}~`,
    };

    const mainFilters = ViewController.getRestrictionsView(Const.Lead);
    const filters = ViewController.filters([...list.filters, kanbanFilter], [], mainFilters, restrict);

    this.setState({
      loadingColumns: { ...loadingColumns, [key]: true }
    });

    const filtersObj = {
      ...list,
      filters,
      pagination: {
        currentPage: page,
        perPage: 10,
      },
    };

    LeadAPI.list( filtersObj, (res) =>  this.onColumnSuccess(key, page, res), (res) => this.onColumnFailed(key, res));
  };
  
  onCloumnCountSuccess = (key, res) => {
    this.setState(prev => ({
      totalCountByColumn: {
        ...prev.totalCountByColumn,
        [key]: res?.count ?? 0
      }
    }))
  }

  onColumnSuccess = (key, page, res) => {
    // CACHE WRITE
    if (!this.columnPageCache[key]) {
      this.columnPageCache[key] = { pages: {}, hasMore: true };
    }

    this.columnPageCache[key].pages[page] = res.data;
    this.columnPageCache[key].hasMore = res.data.length >= 10;


    this.setState(prev => ({
      grouped: {
        ...prev.grouped,
        [key]: {
          ...prev.grouped[key],
          items:
            page === 1
              ? res.data
              : [...prev.grouped[key].items, ...res.data],
        },
      },
      pageByColumn: { ...prev.pageByColumn, [key]: page },
      hasMoreByColumn: {
        ...prev.hasMoreByColumn,
        [key]: res.data.length >= 10,
      },
      loadingColumns: { ...prev.loadingColumns, [key]: false },
    }));
  }

  onColumnFailed = (key, res) => {
    const message = res?.message ? JSON.stringify(res.message) : 'Something went wrong';
    Util.showError(message);
    this.setState(prev => ({
      loadingColumns: { ...prev.loadingColumns, [key]: false }
    }));
  }

  getFieldOptions(fields, field_key) {
    if (!fields || !Object.keys(fields)?.length) return [];
    const fieldFound = Object.values(fields)?.find((field) => field.field_key === field_key);
    if(fieldFound?.options) {
      return fieldFound?.options;
    } else if(fieldFound) {
      // Check in hidden options
      const hiddenOptions = FieldController.getOptions(fieldFound);
      return hiddenOptions ?? [];
    } else {
      return [];
    }
  }

  groupData() {
    const model = this.props.model;
    const selectedKanbanView = this.props.selectedKanbanView;
    const kanbanViews = this.props.kanbanViews;
    const fieldKey = kanbanViews?.find((item) => item.id === selectedKanbanView?.value)?.group_by;
    const groupByOptions = this.getFieldOptions(model.fields, fieldKey);

    const grouped = {};
    const pageByColumn = {};
    const hasMoreByColumn = {};
    const loadingColumns = {};
    
    groupByOptions.forEach((opt) => {
      grouped[opt?.value] = {
        label: opt?.label ?? "",
        items: [],
      };

      pageByColumn[opt.value] = 1;
      hasMoreByColumn[opt.value] = false;
      loadingColumns[opt.value] = false;
    });

    this.setState({ grouped: grouped, pageByColumn, hasMoreByColumn, loadingColumns }, this.loadInitialColumns);
  }

  observeColumnEnd = (key) => {
    if (this.columnObservers[key]) return;

    this.columnObservers[key] = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          this.loadMoreForColumn(key);
        }
      },
      {
        root: this.columnScrollContainers[key]?.querySelector(".column-cards"),
        threshold: 1,
      }
    );

    if (this.columnEndRefs[key]) {
      this.columnObservers[key].observe(this.columnEndRefs[key]);
    }
  };

  loadMoreForColumn = (key) => {
    const { loadingColumns, hasMoreByColumn, pageByColumn } = this.state;
    if (loadingColumns[key] || !hasMoreByColumn[key]) return;
    const nextPage = pageByColumn[key] + 1;
    this.fetchColumnData(key, nextPage);
  }

  onDragStart = ({ active }) => {
    this.setState({
      activeDrag: active.data.current,
    });
  };

  onDragEnd = ({ active, over }) => {
    this.setState({ activeDrag: null });
    if(!over) return;

    const { lead, columnKey: fromColumn } = active.data?.current || {};
    const toColumn = over.data.current?.columnKey;
    // If dragging and dropping to same column
    if (!lead || !fromColumn || !toColumn) return;
    if (fromColumn === toColumn) return;

    // get the selected kanban value (field_key) like "progress", "responsible_person" etc..
    const kanbanField = this.props?.selectedKanbanView?.field_key;
    const modelField = this.props?.model?.fields?.[kanbanField];
    if (!kanbanField || !this.props?.model?.model_key || !modelField) return;

    // This we can use as a backup if api fails
    const previousState = this.state.grouped;
    let updatedLeadData = { ...lead };

    // Field is multiselect
    if(modelField?.field_type?.includes(Const.MultiSelect)) {
      // Calculate multiselect values
      const updatedCurrentVal = `${lead[kanbanField]}`.split(Const.Opt).filter(el => el !== fromColumn).filter(Boolean);
      if(!updatedCurrentVal.includes(toColumn)) {
        updatedCurrentVal.push(toColumn);
      }

      const newValue = updatedCurrentVal.join(Const.Opt);
      updatedLeadData[kanbanField] = `~${newValue}~`;
    } else {
      // Field is single select
      updatedLeadData[kanbanField] = toColumn;
    }

    this.setState(prev => {
      const sourceItems = prev.grouped[fromColumn].items.filter(
        item => item.id !== lead.id
      );
      return {
        grouped: {
          ...prev.grouped,
          [fromColumn]: {
            ...prev.grouped[fromColumn],
            items: sourceItems,
          },
          [toColumn]: {
            ...prev.grouped[toColumn],
            // backend decides final order
            items: [lead, ...prev.grouped[toColumn].items],
          },
        },
      };
    });
    
    GenericModelAPI.save(
      this.props?.model?.model_key, 
      updatedLeadData, 
      (res) => {
        // Refetch column 
        Util.showNotification('Successful', 'Lead saved successfully', 'success');
        this.fetchColumnData(toColumn, 1);
      }, 
      (res) => {
        // Fallback
        const message = res?.message ? JSON.stringify(res.message) : 'Something went wrong';
        Util.showError(message);
        this.setState({
          grouped: previousState,
        }); 
      }
    )
  }

  getDraggedLead = () => {
    if(this.state.activeDrag) {
      return this.state.activeDrag.lead;
    }
    return {};
  }

  getDraggedCardColor = () => {
    if(this.state.activeDrag) {
      return this.state.activeDrag.headerColor;
    }
    return "#64748B";
  }

  renderUI = () => {
    const { grouped, loadingColumns, totalCountByColumn } = this.state;
    const keys = Object.keys(grouped);

    if (!keys.length) return null;
    return (
      <Row className="flex-nowrap mx-0 kanban-container " style={{ gap: 12 }}>
        {keys.map((key, idx) => {
          return (
            <KanbanColumn
              key={key}
              columnKey={key}
              // columnFieldKey={}
              column={grouped[key]}
              totalCount={totalCountByColumn[key]}
              loading={loadingColumns[key]}
              model={this.props.model}
              match={this.props.match}
              onContainerRef={el => {
                if (!this.columnScrollContainers[key]) {
                  this.columnScrollContainers[key] = el;
                }
              }}
              onAnchorRef={el => {
                this.columnEndRefs[key] = el;
                this.observeColumnEnd(key);
              }}
              index={idx}
            />
          );
        })}
      </Row>
    );
  };

  render() {
    const selectedKanbanView = this.props.selectedKanbanView;
    if (!selectedKanbanView) return <NoViewSelected />;
    return (
      <ApiUiController inApiCall={false}>
        <Container fluid className="kanban-container mt-2">
          <DndContext
            collisionDetection={pointerWithin}
            onDragEnd={this.onDragEnd}
            onDragStart={this.onDragStart}
          >
            {this.renderUI()}
            <DragOverlay>
              {this.state.activeDrag ? (
                <KanbanCard
                  lead={this.getDraggedLead()}
                  model={this.props.model}
                  headerColor={this.getDraggedCardColor()}
                  isOverlay
                />
              ) : null}
            </DragOverlay>
          </DndContext>
        </Container>
      </ApiUiController>
    );
  }
}

class NoViewSelected extends React.Component {
  render() {
    return (
      <div className="d-flex align-items-center justify-content-center vh-75 mx-auto py-5">
        <div className="text-center px-4">
          <div className="mb-3">
            <i className="bi bi-kanban fs-1 text-muted"></i>
          </div>

          <h4 className="fw-semibold mb-2">
            No Kanban view selected
          </h4>

          <p className="text-muted mb-4">
            Choose an existing Kanban view to visualize your leads.<br />
            If you don’t have one yet, you can create it from
            <strong> “Settings”</strong>.
          </p>
        </div>
      </div>
    )
  }
}

export default KanbanUI;
